/* oxlint-disable regexp/use-ignore-case */
import type { FunctionDescriptorOutput } from '@festina/configuration-general-types/configuration'
import { isEmpty, type Optional } from '@festina-life/core'
import {
  extractFunction,
  type FunctionMetadata,
  type ParameterMetadata,
} from '@festina-life/fplus-monaco'

import { extractCollectionType } from '../../context/typeOptions'
import type { CollectionType } from '../fplusExpression/types'

export const functionNameWithModule = (source: Optional<string>) => {
  const functionMetadata = extractFunctionWithCache(source ?? '')

  if (!functionMetadata) {
    return '-'
  }

  const modulePrefix = functionMetadata.module
    ? `${functionMetadata.module}.`
    : ''

  return `${modulePrefix}${functionMetadata.name}`
}

export const functionCall = (source: Optional<string>) => {
  const functionMetadata = extractFunctionWithCache(source ?? '')

  if (!functionMetadata) {
    return '-'
  }

  const nameWithModule = functionNameWithModule(source)

  return `${nameWithModule}(${functionMetadata.parameters.map((p) => p.name).join(', ')})${conditionalReturnTypeString(functionMetadata)}`
}

const parameterMetaDataToString = (parameter: ParameterMetadata) => {
  const defaultValue = parameter.hasDefaultValue
    ? `= ${parameter.defaultValue}`
    : ''
  return `${parameter.name}: ${parameter.type} ${defaultValue}`.trim()
}

const conditionalReturnTypeString = (functionMetadata: FunctionMetadata) => {
  if (!functionMetadata.returnType) {
    return ''
  }
  return ` : ${functionMetadata.returnType}`
}

export const unnamedFunctionSignature = (source: Optional<string>) => {
  const functionMetadata = extractFunctionWithCache(source ?? '')

  if (!functionMetadata) {
    return undefined
  }

  return `( ${functionMetadata.parameters
    .map(parameterMetaDataToString)
    .join(', ')} )${conditionalReturnTypeString(functionMetadata)}`.replace(
    /\s+/g,
    ' ',
  )
}

export const functionIdentifier = ({
  descriptor,
  source,
}: {
  descriptor?: Optional<FunctionDescriptorOutput>
  source: Optional<string>
}) => {
  const identifierSuffix = descriptor?.signature
    ? functionDeclaration(descriptor.signature) // We parse the signatures through the function declaration function to ensure that the spacing is consistent
    : functionDeclaration(source)

  return `[function]${identifierSuffix ?? '-'}`.replace(/\s/g, '')
}

export const functionDeclaration = (source: Optional<string>) => {
  const functionMetadata = extractFunctionWithCache(source ?? '')

  if (!functionMetadata) {
    return undefined
  }

  return functionDeclarationFromMetadata(functionMetadata)
}

export const functionDeclarationFromMetadata = (
  functionMetadata: FunctionMetadata,
) => {
  const qualifiedName = functionMetadata.module
    ? `${functionMetadata.module}.${functionMetadata.name}`
    : functionMetadata.name
  const parameterDeclaration = functionMetadata.parameters
    .map(parameterMetaDataToString)
    .join(', ')

  return `${qualifiedName}( ${parameterDeclaration}${parameterDeclaration.length ? ' ' : ''}) : ${functionMetadata.returnType}`
}

export const functionBody = (source: string) => {
  const functionMetadata = extractFunctionWithCache(source ?? '')

  if (isEmpty(functionMetadata?.body)) {
    return source
  }

  return functionMetadata.body
}

export const functionSignatureToFunctionCallWithReturnType = (
  signature: string,
) => {
  const functionMetadata = extractFunctionWithCache(signature)

  if (!functionMetadata) {
    return undefined
  }

  return `${functionNameWithModule(signature)}( ${(
    functionMetadata.parameters ?? []
  )
    .map(({ name }) => name)
    .join(', ')} )${conditionalReturnTypeString(functionMetadata)}`.replace(
    /\s+/g,
    ' ',
  )
}

/**
 * Trims off the model name from a function call string if it exists.
 */
export const functionCallWithoutModelName = (functionCall: string) => {
  const matches = functionCall.match(
    /^([a-z][a-z0-9]*)\.(?<nameWithoutModel>.*)/i,
  )

  if (!matches?.groups) {
    return functionCall
  }

  const { nameWithoutModel } = matches.groups

  return nameWithoutModel ?? functionCall
}

// Parsing FPlus code is expensive so we use a session cache in which to store the parsed functions to avoid parsing the same code multiple times
const parsedFunctionsMap = new Map<string, Optional<FunctionMetadata>>()

export const extractFunctionWithCache = (source: string) => {
  if (parsedFunctionsMap.has(source)) {
    return parsedFunctionsMap.get(source)
  }

  const functionMetadata = extractFunction(source)
  parsedFunctionsMap.set(source, functionMetadata)
  return functionMetadata
}

export const functionReturnTypeFromSignature = (
  signature: string,
): Optional<{
  returnType: string
  collectionType: Optional<CollectionType>
}> => {
  const functionMetadata = extractFunctionWithCache(signature)

  if (!functionMetadata?.returnType) {
    return undefined
  }

  const matchedCollectionType = extractCollectionType(
    functionMetadata.returnType,
  )

  return (
    matchedCollectionType ?? {
      returnType: functionMetadata.returnType,
      collectionType: undefined,
    }
  )
}
